import { type ComponentProps } from 'react';
import { Button } from '@herit-fe/heritage-ui';
import type { ConsentDocKind } from '@/shared/data-types';
import { UI_TEXT } from '@/shared/languages';
import { ContentColumn, type WidthVariant } from './content-column';

/** 푸터가 거는 약관 2종 — 원문 소스는 v3 consents(비인증). 환불정책은 여기 두지 않는다. */
const FOOTER_CONSENT_DOCS: ReadonlyArray<{ kind: ConsentDocKind; label: string }> = [
  { kind: 'terms', label: UI_TEXT.AGREEMENT_TERMS_TITLE },
  { kind: 'privacy', label: UI_TEXT.AGREEMENT_PRIVACY_TITLE },
];

/** 약관·로그아웃이 공유하는 ghost/xs 스타일. */
function FooterButton(props: ComponentProps<typeof Button>) {
  return <Button variant="ghost" size="xs" className="h-auto p-1 font-bold" {...props} />;
}

export interface StandaloneFooterProps {
  /** 로그아웃 핸들러. 주어지면 로그아웃 버튼을 렌더(노출 판단은 호출처), 없으면 미노출. */
  onLogout?: () => void;
  /** 약관 원문 열기 핸들러(kind). presentational 이라 모달/페이지 분기는 호출처가 주입. */
  onOpenConsentDoc?: (kind: ConsentDocKind) => void;
  width?: WidthVariant;
}

/**
 * Standalone 표면 푸터 — HERIT CORP 법인 정보 + 약관 + (onLogout 시) 로그아웃.
 * presentational — feature 미의존. 로그아웃·약관 열기 동작은 호출처가 `onLogout`·`onOpenConsentDoc`
 * 로 주입(app/standalone-layout 이 약관 모달 배선).
 */
export function StandaloneFooter({ onLogout, onOpenConsentDoc, width }: StandaloneFooterProps) {
  return (
    <ContentColumn
      as="footer"
      width={width}
      className="text-xs leading-relaxed text-muted-foreground pt-4"
    >
      <h3 className="text-sm font-medium text-foreground">{UI_TEXT.BRAND_FOOTER_COMPANY_NAME}</h3>
      <p className="mt-2">{UI_TEXT.BRAND_FOOTER_COMPANY_INFO}</p>
      <div className="mt-2 flex gap-1">
        {FOOTER_CONSENT_DOCS.map(({ kind, label }) => (
          <FooterButton key={kind} onClick={() => onOpenConsentDoc?.(kind)}>
            {label}
          </FooterButton>
        ))}
        {onLogout && <FooterButton onClick={onLogout}>{UI_TEXT.AUTH_LOGOUT}</FooterButton>}
      </div>
    </ContentColumn>
  );
}
